import React from 'react';

import { TableCell, TableRow, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import '../../styles.scss';
import { DATE_FORMAT } from '../../../../constants/date.constant';
import { IPreInscription } from '../../../../types/pre-inscription.type';

const sxCell: SxProps<Theme> = { textAlign: 'center' };

interface IProps {
  inscription: IPreInscription;
}

const stringKeys: Array<keyof Omit<IPreInscription, 'student'>> = [
  'schoolLevel',
  'reason',
  'academy',
  'department',
  'situation',
  'meetingDate',
  'dossierComment',
  'depasseComment',
  'responseDa',
  'responseDaComment',
  'responseDaDate',
  'inscription',
  'envoiRapo',
  'envoiRapoComment',
  'envoiRapoDate',
  'responseRapo',
  'responseRapoComment',
  'responseRapoDate',
  'recoursTa',
  'recoursTaComment',
  'filingDate',
  'referredComment',
  'audienceDate',
  'responseTa',
  'responseTaComment',
  'refundComment',
  'processedBy',
];

const translateFields = [
  'responseDa',
  'inscription',
  'envoiRapo',
  'responseRapo',
  'recoursTa',
  'responseTa',
  'refundComment',
  'processedBy',
];

const PreInscriptionListItem = React.memo(({ inscription }: IProps) => {
  const { t } = useTranslation();
  const { student } = inscription;
  const { parent } = student || {};

  const fields = React.useMemo(
    () =>
      stringKeys.map((key) => {
        if (translateFields.includes(key)) {
          return {
            key,
            name: inscription?.[key] ? t(`pre_inscriptions.${inscription[key]}`) : '',
          };
        }

        if (key === 'academy') {
          return {
            key,
            name: inscription.academy.name,
          };
        }

        if (key === 'reason') {
          return {
            key,
            name: inscription.reason.name,
          };
        }

        if (key === 'situation') {
          return {
            key,
            name: inscription.situation?.name ?? '',
          };
        }

        return {
          key,
          name: inscription[key],
        };
      }),
    [inscription, t],
  );

  return (
    <TableRow>
      <TableCell sx={sxCell} className="sticky-col sticky-school">
        {t(`school.${inscription.school}`)}
      </TableCell>

      <TableCell sx={sxCell} className="sticky-col sticky-date">
        {inscription.paymentDate ? dayjs(inscription.paymentDate).format(DATE_FORMAT) : ''}
      </TableCell>

      <TableCell sx={sxCell} className="sticky-col sticky-year">
        {inscription.schoolYear}
      </TableCell>

      <TableCell sx={sxCell} className="sticky-col sticky-parent">
        {parent?.firstName} {parent?.lastName}
      </TableCell>

      <TableCell sx={sxCell}>{parent?.email}</TableCell>
      <TableCell sx={sxCell}>{t(`general.${inscription.registrationType}_item`)}</TableCell>

      <TableCell sx={sxCell}>
        {student?.firstName} {student?.lastName}
      </TableCell>

      {fields.map(({ key, name }) => (
        <TableCell key={key} sx={sxCell}>
          {name}
        </TableCell>
      ))}
    </TableRow>
  );
});

export default PreInscriptionListItem;
